import { useCallback } from "react"
import { Collapse } from "components/ui/collapse/Collapse"
import { TableConfiguration } from "components/ui/tables/TableConfiguration/TableConfiguration"
import { CompetitorDropdown } from "components/ui/dropdown/CompetitorDropdown/CompetitorDropdown"
import { PencilIcon } from "components/ui/icons/objectsTable/dropdown/PencilIcon"
import type { ColumnsType } from "antd/es/table"
import type { MenuProps } from "antd"
import { useGetCompetitors, useGetCompetitorsModelNumbers } from "./api/competitors/competitors.hook"

type CompetitorInfo = {
	competitor_id: number
	competitor_name: string
	competitor_model_numbers: string[]
}

type StevensModelData = {
	stevens_id: number
	stevens_model_number: string
	competitors: CompetitorInfo[]
}

type SubCategory = {
	[subcategoryName: string]: StevensModelData[]
}

type TopLevelCategory = {
	[topLevelCategoryName: string]: SubCategory
}

type TableRowData = {
	key: string
	isSubcategoryHeader?: boolean
	subcategoryName?: string
	stevens_model_number?: string
	stevens_id?: number
	[competitorName: string]: string | boolean | number | undefined
}

type UseCollapseCompetitorsProps = {
	getCompetitorDropdownItems: (competitorId: number, competitorName: string) => MenuProps["items"]
	getStevensModelDropdownItems: (stevensId: number, stevensModelNumber: string) => MenuProps["items"]
	onEditCompetitorModelNumbers: (
		competitorId: number,
		competitorName: string,
		stevensId: number,
		stevensModelNumber: string,
		modelNumbers: string[],
	) => void
}

export const useCollapseCompetitors = ({
	getCompetitorDropdownItems,
	getStevensModelDropdownItems,
	onEditCompetitorModelNumbers,
}: UseCollapseCompetitorsProps) => {
	const { data: competitorsModelNumbers, isPending: isGetCompetitorsModelNumbersPending } =
		useGetCompetitorsModelNumbers()
	const { data: competitors, isPending: isGetCompetitorsPending } = useGetCompetitors()

	const competitorsPending = isGetCompetitorsModelNumbersPending || isGetCompetitorsPending

	// Generate table data and columns for a top-level category
	const generateTableForTopLevelCategory = useCallback(
		(_topLevelCategoryName: string, subCategories: SubCategory) => {
			// Use competitors from the separate endpoint
			const uniqueCompetitors = competitors?.map((comp: { id: number; name: string }) => comp.name) || []
			const competitorsMap = new Map<string, number>(
				competitors?.map((comp: { id: number; name: string }) => [comp.name, comp.id]) || [],
			)

			// Build table data with subcategory headers and model rows
			const tableData: TableRowData[] = []

			Object.entries(subCategories).forEach(([subcategoryName, items]) => {
				// Add subcategory header row
				tableData.push({
					key: `header-${subcategoryName}`,
					isSubcategoryHeader: true,
					subcategoryName: subcategoryName,
				})

				// Add model number rows for this subcategory
				items.forEach((item) => {
					const competitorMap = new Map<string, string[]>()
					item.competitors.forEach((comp) => {
						competitorMap.set(comp.competitor_name, comp.competitor_model_numbers)
					})

					const rowData: TableRowData = {
						key: `model-${item.stevens_id}`,
						isSubcategoryHeader: false,
						stevens_model_number: item.stevens_model_number,
						stevens_id: item.stevens_id,
					}

					// Add competitor columns
					uniqueCompetitors.forEach((competitorName: string) => {
						const modelNumbers = competitorMap.get(competitorName) || []
						rowData[competitorName] = modelNumbers.join(", ")
					})

					tableData.push(rowData)
				})
			})

			// Build columns
			const columns: ColumnsType<TableRowData> = [
				{
					title: "STEVENS",
					dataIndex: "stevens_model_number",
					key: "stevens",
					width: 200,
					onCell: (record) => {
						if (record.isSubcategoryHeader) {
							return {
								colSpan: uniqueCompetitors.length + 1,
								style: { backgroundColor: "#F2F3F7" },
							}
						}
						return {}
					},
					render: (text, record) => {
						if (record.isSubcategoryHeader) {
							return <div>{record.subcategoryName}</div>
						}

						return (
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
								<span>{text}</span>
								{record.stevens_id && (
									<CompetitorDropdown items={getStevensModelDropdownItems(record.stevens_id, text)} />
								)}
							</div>
						)
					},
				},
				...uniqueCompetitors.map((competitorName: string) => {
					const competitorId = competitorsMap.get(competitorName)!
					return {
						title: (
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
								<span>{competitorName}</span>
								<CompetitorDropdown items={getCompetitorDropdownItems(competitorId, competitorName)} />
							</div>
						),
						dataIndex: competitorName,
						key: competitorName,
						onCell: (record: TableRowData) => {
							if (record.isSubcategoryHeader) {
								return {
									colSpan: 0,
								}
							}
							return {}
						},
						render: (text: string, record: TableRowData) => {
							if (record.isSubcategoryHeader) {
								return null
							}

							// Parse model numbers from text (they are joined with ", ")
							const modelNumbers = text ? text.split(", ").filter(Boolean) : []

							return (
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										width: "100%",
									}}
								>
									<span>{text || ""}</span>
									<button
										onClick={() =>
											onEditCompetitorModelNumbers(
												competitorId,
												competitorName,
												record.stevens_id || 0,
												record.stevens_model_number || "",
												modelNumbers,
											)
										}
										style={{
											background: "none",
											border: "none",
											cursor: "pointer",
											padding: "4px",
											display: "flex",
											alignItems: "center",
										}}
										aria-label={`Edit ${competitorName} model numbers for ${record.stevens_model_number}`}
									>
										<PencilIcon strokeColor="#DEE0EB" />
									</button>
								</div>
							)
						},
					}
				}),
			]

			return { columns, data: tableData }
		},
		[getCompetitorDropdownItems, getStevensModelDropdownItems, onEditCompetitorModelNumbers, competitors],
	)

	// Generate collapse items for top-level categories within a super category
	const generateSubCollapseItems = useCallback(
		(topLevelCategoryData: TopLevelCategory, superCategory: string) => {
			return Object.entries(topLevelCategoryData).map(([topLevelCategoryName, subCategories]) => {
				const { columns, data } = generateTableForTopLevelCategory(topLevelCategoryName, subCategories)

				return {
					key: `${superCategory}-${topLevelCategoryName}`,
					label: topLevelCategoryName,
					"data-testid": "collapse-sub-items",
					children: (
						<div className="TableConfiguration">
							<TableConfiguration columns={columns} data={data} scrollY={600} bordered={true} />
						</div>
					),
				}
			})
		},
		[generateTableForTopLevelCategory],
	)

	// Generate top-level collapse items for super categories
	const generateCollapseItems = useCallback(() => {
		if (!competitorsModelNumbers) return []

		return Object.entries(competitorsModelNumbers).map(([superCategory, categoryData]) => {
			// Handle "Without category" case (array of items directly)
			if (Array.isArray(categoryData)) {
				// Create a simple table for "Without category"
				const subCategories = { "Without category": categoryData }
				const { columns, data } = generateTableForTopLevelCategory("Without category", subCategories)

				return {
					key: superCategory,
					label: superCategory,
					"data-testid": "collapse-items",
					children: (
						<div style={{ paddingLeft: "40px" }}>
							<TableConfiguration columns={columns} data={data} scrollY={600} />
						</div>
					),
				}
			}

			// Handle normal nested structure
			return {
				key: superCategory,
				label: superCategory,
				"data-testid": "collapse-items",
				children: (
					<div style={{ paddingLeft: "40px" }}>
						<Collapse items={generateSubCollapseItems(categoryData as TopLevelCategory, superCategory)} />
					</div>
				),
			}
		})
	}, [generateSubCollapseItems, generateTableForTopLevelCategory, competitorsModelNumbers])

	return { generateCollapseItems, competitorsPending }
}
